/**
 * What beat 6's cues mean in the loading yard.
 *
 * The story owns the order of these moments and `main.ts` owns the renderer,
 * audio engine, physics and sequencer. This class owns the small deterministic
 * piece between them: weather, one watcher, and the two cuts which keep the
 * player in the beat when an authored patience clock expires.
 */

import * as THREE from 'three';
import { mulberry32 } from '@/audio/prng';
import { findKestnerParts, type KestnerParts } from '@/models/env/kestner';
import { disposeAsset } from '@/models/materials';
import { getAsset } from '@/models/registry';
import { STORM, Storm, type StormState } from '@/systems/storm';
import type { StealthSnapshot, Watcher } from '@/systems/stealth';
import { YARD_CUES, YARD_IDS } from '@/story/act1/loadingYard';
import type { Cue } from '@/story/types';
import { FadeOverlay } from '@/world/fadeOverlay';
import {
  TRAILER_AT,
  YARD,
  YARD_SPAWN,
  trailerLocalToWorld,
} from '@/world/loadingYard';

/** In the flood, beside the foot of the stock race, facing the race. */
const KESTNER_AT = { x: 10.2, z: 0.8, facing: -Math.PI / 2 } as const;

/** The calm cone leaves the crossing alone; the alert cone makes its middle dangerous. */
const KESTNER_CALM = { coneHalfAngle: 0.384, range: 9 } as const;
const KESTNER_ALERT = { coneHalfAngle: 0.960, range: 18 } as const;

/** Eye height on a 1.75 m man. */
const KESTNER_EYE = 1.62;

/** How far past the flash he keeps sweeping before returning to the clipboard. */
const SWEEP_TAIL = 1.5;

/**
 * The same seed `createSky` uses for this room, so a screenshot of the sky and
 * a replay of the strikes belong to the same authored weather.
 */
const YARD_STORM_SEED = 6;

/** The head turns away from the race into the middle of the yard, then returns. */
const SWEEP_ARC = -0.75;

/** The asset rests over its board; alert is the small, readable lift from it. */
const HEAD_DOWN = 0.34;
const HEAD_UP = -0.16;

/** The authored escape from the bin row, clear of its single continuous collider. */
const YARD_CLIMB_OUT = { x: -9.5, y: 0, z: -5.6, facing: 0 } as const;

/** Beat 5's interior floor, retained so the hand-over does not put her through the bin. */
const BIN_FLOOR_Y = 0.22;

/** The cue and the release are deliberately separated so stage 1 has its opening shot. */
const CLIMB_OUT = 4;

/** The sparse trailer sounds are implication, not a queue of animals this beat does not own. */
const RACE_CLANG_MIN = 4;
const RACE_CLANG_MAX = 13;

/** Horizon thunder is weather texture only: deterministic, but never mechanical. */
const FAR_THUNDER_MIN = 18;
const FAR_THUNDER_MAX = 40;

/**
 * Capture is a timeline rather than a property of the fade, because the shared
 * fade converges exponentially. These are the same D-084 policy values as the
 * service corridor, copied here while only two beats need the mechanism.
 */
const CAPTURE = { blackAt: 2.5, backAt: 1.2 } as const;

/** Beat 5's bin shot inverted: from under a lid, she sees weather before the yard. */
const BIN_CAMERA = { y: 0.34, lookY: 2.35, lookAhead: 2.6, fov: 52 } as const;

/**
 * Snout height under steel; the reward is looking back out at the dangerous
 * yard. `localX`/`localZ` are TRAILER-local metres — see `framing()`.
 */
const UNDER_TRAILER_CAMERA = { localX: 0.8, localZ: -1, y: 0.3, fov: 58 } as const;

export interface Framing {
  position: { x: number; y: number; z: number };
  lookAt: { x: number; y: number; z: number };
  fov: number;
}

type Vec = { x: number; y: number; z: number };

/** `window.__rosalie.yardDebug`: the director's view, its fade, the mix's shelter and every strike's sim time. */
export type YardDebug = YardDirector['debug'] & {
  readonly fade: number;
  readonly sheltered: boolean;
  readonly strikes: readonly number[];
};

export interface YardDirectorOptions {
  /** Thunder, at the authored delay after a strike. `near` is overhead. */
  onThunder: (distance: 'near' | 'far', at: Vec) => void;
  /** Something heavy shifts against a steel panel. Irregular, sparse. */
  onRaceClang: (at: Vec) => void;
  /** The bin lid, once, as she comes out of it. */
  onLid: (at: Vec) => void;
  /** He has seen her and is whistling somebody over. Fired once per capture. */
  onWhistle: (at: Vec) => void;
  /** Whether she is under steel. This is a level, so fixed steps report it every time. */
  onSheltered: (sheltered: boolean) => void;
}

export class YardDirector {
  readonly #options: YardDirectorOptions;
  readonly #fadeOverlay: FadeOverlay;
  readonly #kestner: THREE.Object3D;
  readonly #parts: KestnerParts;
  readonly #storm: Storm;
  readonly #farThunderRandom = mulberry32(YARD_STORM_SEED + 1);
  readonly #raceClangRandom = mulberry32(YARD_STORM_SEED + 2);

  /**
   * This identity is deliberately stable. `main.ts` gives this exact object to
   * `StealthSystem`; returning a copy would leave that system sampling the cone
   * from construction while the visible Kestner appeared to sweep.
   */
  readonly #watcher: Watcher = {
    id: YARD_IDS.kestner,
    position: { x: KESTNER_AT.x, y: 0, z: KESTNER_AT.z },
    facing: KESTNER_AT.facing,
    coneHalfAngle: KESTNER_CALM.coneHalfAngle,
    range: KESTNER_CALM.range,
    eyeHeight: KESTNER_EYE,
  };

  #farThunderIn: number | null;
  #raceClangIn: number | null = null;
  #raceAdvance = false;
  #teachTriggered = false;
  #teachSawFlash = false;
  #flashTaught = false;
  #alert = false;
  #alertElapsed = 0;
  #carrying = false;
  #climbOutIn: number | null = null;
  #outOfTheBin = false;
  #underTrailer = false;
  #placeUnderTrailer = false;
  #underIt = false;
  #captureLive = false;
  #captureStage: 'out' | 'in' | null = null;
  #captureElapsed = 0;
  #captureRespawn: ((to: typeof YARD_CLIMB_OUT) => void) | null = null;
  #ended = false;

  constructor(scene: THREE.Object3D, options: YardDirectorOptions) {
    this.#options = options;
    this.#fadeOverlay = new FadeOverlay();
    this.#storm = new Storm({ seed: YARD_STORM_SEED, distance: 0.35 });
    this.#farThunderIn = this.#sampleFarThunder();

    const asset = getAsset('kestner');
    if (!asset) throw new Error('yard director: kestner asset is not registered');
    this.#kestner = asset.create();
    this.#kestner.name = 'kestner-figure';
    this.#kestner.position.set(KESTNER_AT.x, 0, KESTNER_AT.z);
    this.#kestner.rotation.y = KESTNER_AT.facing;
    this.#kestner.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (mesh.isMesh) mesh.castShadow = true;
    });
    scene.add(this.#kestner);
    this.#parts = findKestnerParts(this.#kestner);
    // His stance is NOT set here, and that is the seam rather than an omission.
    // The weight-on-one-hip pose is authored in `kestner.ts` because it is what
    // the figure IS — the silhouette gate is passed on it and beat 11 gets the
    // same man — and a director that re-posed his hips would be a second place
    // that decides how he stands, in the wrong sign, the first time somebody
    // adjusted one of them.
  }

  get fade(): number {
    return this.#fadeOverlay.value;
  }

  get flash(): number {
    return this.#storm.state.flash;
  }

  get stormState(): Readonly<StormState> {
    return this.#storm.state;
  }

  /** A world fact rather than a cue: the teach envelope has entirely finished. */
  get flashTaught(): boolean {
    return this.#flashTaught;
  }

  get watcher(): Watcher {
    return this.#watcher;
  }

  /** Gate 6 in `tests/e2e/yard-beat.spec.ts` asserts this cone delta, not a level. */
  get alert(): boolean {
    return this.#alert;
  }

  get carrying(): boolean {
    return this.#carrying;
  }

  get carryPose(): { x: number; y: number; z: number; facing: number } {
    return { x: YARD_SPAWN.x, y: BIN_FLOOR_Y, z: YARD_SPAWN.z, facing: 0 };
  }

  get setDownAt(): typeof YARD_CLIMB_OUT {
    return YARD_CLIMB_OUT;
  }

  /** This rises only once; callers use that edge to reset the body exactly once. */
  get outOfTheBin(): boolean {
    return this.#outOfTheBin;
  }

  /**
   * Where the anti-softlock puts her: the middle of the trailer, in the
   * trailer's OWN axes.
   *
   * Not the centre of `TRAILER_SHADOW_REGION`, which is what this was and which
   * happened to be the same point only because that region is symmetric about
   * the trailer. The region is an axis-aligned box around a unit parked 0.35 rad
   * off square (D-108), so reaching into it for a *place* is reaching into the
   * wrong volume — two of its corners are open yard. `trailerLocalToWorld(0, 0)`
   * is the deck's centre by construction and stays there if the truck is ever
   * re-parked.
   */
  get underTrailerAt(): { x: number; y: number; z: number; facing: number } {
    const at = trailerLocalToWorld(0, 0);
    return { x: at.x, y: 0, z: at.z, facing: 0 };
  }

  /** This rises only once, at the same authored black cut that places her under steel. */
  get placeUnderTrailer(): boolean {
    return this.#placeUnderTrailer;
  }

  get captureLive(): boolean {
    return this.#captureLive;
  }

  /** The camera belongs to the bin or the under-trailer reward, and nowhere else. */
  get cameraHeld(): boolean {
    return this.framing() !== null;
  }

  get ended(): boolean {
    return this.#ended;
  }

  /** What the beat's gates read instead of trying to infer a state from pixels. */
  get debug(): {
    flash: number;
    tell: boolean;
    alert: boolean;
    coneHalfAngle: number;
    range: number;
    facing: number;
    captureLive: boolean;
    carrying: boolean;
    raceAdvance: boolean;
    ended: boolean;
  } {
    return {
      flash: this.flash,
      tell: this.#storm.state.tell,
      alert: this.#alert,
      coneHalfAngle: this.#watcher.coneHalfAngle,
      range: this.#watcher.range,
      facing: this.#watcher.facing,
      captureLive: this.#captureLive,
      carrying: this.#carrying,
      raceAdvance: this.#raceAdvance,
      ended: this.#ended,
    };
  }

  handle(cue: Cue): void {
    if (this.#ended) return;
    switch (cue.id) {
      case YARD_CUES.open:
        this.#fadeOverlay.setTarget(0);
        this.#carrying = true;
        break;

      case YARD_CUES.climbOut:
        this.#climbOutIn = CLIMB_OUT;
        break;

      case YARD_CUES.theCount:
        // The race is empty: Kestner counts against a clipboard while the last
        // animals shift inside the trailer. That absence is this beat's cut, not
        // an excuse to construct a standing finisher variant before beat 12.
        this.#raceAdvance = true;
        this.#raceClangIn = this.#sampleRaceClang();
        break;

      case YARD_CUES.offerKestner:
        break;

      case YARD_CUES.teachFlash:
        this.#teachTriggered = true;
        this.#storm.trigger();
        break;

      case YARD_CUES.crossing:
        this.#storm.start();
        this.#captureLive = true;
        break;

      case YARD_CUES.putHerUnder:
        this.#captureLive = false;
        this.#underTrailer = true;
        this.#placeUnderTrailer = true;
        this.#fadeOverlay.snapToBlackAndFadeIn();
        break;

      case YARD_CUES.underIt:
        this.#captureLive = false;
        this.#underTrailer = true;
        this.#underIt = true;
        break;

      case YARD_CUES.fadeOut:
        this.#fadeOverlay.setTarget(1);
        break;

      case YARD_CUES.end:
        // The final built beat has no `next`, so disposal is not a lifecycle
        // guarantee. D-087/D-090 require the fixed step itself to honour this
        // flag; otherwise thunder and trailer sounds continue behind black.
        this.#ended = true;
        this.#captureLive = false;
        this.#storm.stop();
        this.#farThunderIn = null;
        this.#raceClangIn = null;
        break;

      default:
        break;
    }
  }

  /** Fixed-step state only: schedules, watcher state, body ownership and capture. */
  step(dt: number): void {
    if (this.#ended) return;

    const storm = this.#storm.step(dt);
    if (storm.thunder) {
      this.#options.onThunder('near', {
        x: (YARD.minX + YARD.maxX) / 2,
        y: 12,
        z: (YARD.minZ + YARD.maxZ) / 2,
      });
    }

    if (this.#teachTriggered) {
      if (storm.flashing) this.#teachSawFlash = true;
      else if (this.#teachSawFlash) this.#flashTaught = true;
    }

    this.#stepAlert(dt, storm);
    this.#stepClimbOut(dt);
    this.#stepFarThunder(dt);
    this.#stepRaceClang(dt);
    this.#stepCapture(dt);
    this.#options.onSheltered(this.#carrying || this.#underTrailer);
  }

  /**
   * Presentation catches up here and never writes the watcher or story state.
   * In particular, the visible head and facing read the fixed-step decisions;
   * putting the sweep here would make stealth frame-rate dependent.
   */
  update(frameDt: number): void {
    const dt = Number.isFinite(frameDt) ? Math.max(0, Math.min(0.1, frameDt)) : 0;
    this.#kestner.rotation.y = this.#watcher.facing;
    const target = this.#alert ? HEAD_UP : HEAD_DOWN;
    this.#parts.neck.rotation.x += (target - this.#parts.neck.rotation.x) * (1 - Math.exp(-8 * dt));
    this.#fadeOverlay.update(dt);
  }

  framing(): Framing | null {
    if (this.#carrying) {
      return {
        position: { x: YARD_SPAWN.x, y: BIN_CAMERA.y, z: YARD_SPAWN.z },
        lookAt: { x: YARD_SPAWN.x + 0.35, y: BIN_CAMERA.lookY, z: YARD_SPAWN.z + BIN_CAMERA.lookAhead },
        fov: BIN_CAMERA.fov,
      };
    }
    if (!this.#underIt) return null;
    // **In the trailer's own axes, and the first draft was not.** It framed
    // `TRAILER_SHADOW_REGION.min + a bit`, which is a corner of the axis-aligned
    // bounds of a box parked askew — open yard, in the rain, with nothing over
    // the lens at all. The whole point of this shot is that there is steel above
    // it. Local (0.8, −1.0) is under the deck, one bay in from the near side and
    // a metre toward the bogie, so the wheels are at the edge of frame: rain on
    // concrete, boots, wheels (`docs/beat-6-plan.md`, stage 5).
    const at = trailerLocalToWorld(UNDER_TRAILER_CAMERA.localX, UNDER_TRAILER_CAMERA.localZ);
    return {
      position: { x: at.x, y: UNDER_TRAILER_CAMERA.y, z: at.z },
      // Out at him, across the yard she has just crossed. He is still counting,
      // and she is looking at him from underneath a lorry: the last thing beat 6
      // says is that nothing about the yard changed because she got across it.
      lookAt: { x: KESTNER_AT.x, y: 0.9, z: KESTNER_AT.z },
      fov: UNDER_TRAILER_CAMERA.fov,
    };
  }

  /**
   * Caught, or not. Called once after the stealth system's fixed step.
   *
   * Kestner does not chase, run, grab or touch her. The whistle is the same
   * handler-whistle that called the dog off in beat 4, inverted here: he calls
   * somebody over because he has written something down.
   */
  capture(snapshot: StealthSnapshot, moveSelf: (to: typeof YARD_CLIMB_OUT) => void): void {
    // **`this.fade` is deliberately NOT consulted, and `corridorDirector`'s
    // version of this line does consult it.** `fade` is written in `update()`,
    // which runs at display rate; `capture()` is called from `fixedUpdate`. A
    // fixed-step decision taken on a render-rate value is frame-rate dependent,
    // which is D-045's whole prohibition. Found on independent review.
    //
    // Nothing is lost by dropping it. What it guarded against was starting a
    // capture while the screen was already dark, and both of those states are
    // already fixed-step facts here: `#captureStage` covers one in flight, and
    // `#captureLive` is false everywhere the beat fades on purpose — it is
    // raised only by `crossing` and dropped by `putHerUnder`, `underIt` and
    // `end`.
    if (!this.#captureLive || this.#captureStage !== null) return;
    if (!snapshot.watchers.some((watcher) => watcher.state === 'detected')) return;

    // `StealthSystem` already raised the alarm on this edge. Resetting or adding
    // to it here would make one sighting have two unrelated consequences.
    this.#options.onWhistle({ x: KESTNER_AT.x, y: KESTNER_EYE, z: KESTNER_AT.z });
    this.#fadeOverlay.setTarget(1);
    this.#captureStage = 'out';
    this.#captureElapsed = 0;
    this.#captureRespawn = moveSelf;
  }

  dispose(): void {
    this.#fadeOverlay.dispose();
    this.#kestner.removeFromParent();
    disposeAsset(this.#kestner);
  }

  #stepAlert(dt: number, storm: Readonly<StormState>): void {
    if (storm.tell && !this.#alert) {
      this.#alert = true;
      this.#alertElapsed = 0;
    }

    if (!this.#alert) {
      this.#watcher.coneHalfAngle = KESTNER_CALM.coneHalfAngle;
      this.#watcher.range = KESTNER_CALM.range;
      this.#watcher.facing = KESTNER_AT.facing;
      return;
    }

    this.#alertElapsed += dt;
    const duration = STORM.decayStart + STORM.decayDuration + SWEEP_TAIL;
    if (this.#alertElapsed >= duration) {
      this.#alert = false;
      this.#watcher.coneHalfAngle = KESTNER_CALM.coneHalfAngle;
      this.#watcher.range = KESTNER_CALM.range;
      this.#watcher.facing = KESTNER_AT.facing;
      return;
    }

    const u = this.#alertElapsed / duration;
    this.#watcher.coneHalfAngle = KESTNER_ALERT.coneHalfAngle;
    this.#watcher.range = KESTNER_ALERT.range;
    this.#watcher.facing = KESTNER_AT.facing + Math.sin(Math.PI * u) * SWEEP_ARC;
  }

  #stepClimbOut(dt: number): void {
    if (this.#climbOutIn === null || !this.#carrying) return;
    this.#climbOutIn -= dt;
    if (this.#climbOutIn > 0) return;
    this.#climbOutIn = null;
    this.#carrying = false;
    this.#outOfTheBin = true;
    this.#options.onLid({ x: YARD_SPAWN.x, y: 1, z: YARD_SPAWN.z });
  }

  #stepFarThunder(dt: number): void {
    if (this.#farThunderIn === null) return;
    this.#farThunderIn -= dt;
    while (this.#farThunderIn !== null && this.#farThunderIn <= 0) {
      this.#options.onThunder('far', { x: YARD.maxX + 24, y: 8, z: YARD.maxZ + 24 });
      this.#farThunderIn += this.#sampleFarThunder();
    }
  }

  #stepRaceClang(dt: number): void {
    if (this.#raceClangIn === null) return;
    this.#raceClangIn -= dt;
    while (this.#raceClangIn !== null && this.#raceClangIn <= 0) {
      this.#options.onRaceClang({ x: TRAILER_AT.x, y: 1.4, z: TRAILER_AT.z });
      this.#raceClangIn += this.#sampleRaceClang();
    }
  }

  #stepCapture(dt: number): void {
    if (this.#captureStage === null) return;
    this.#captureElapsed += dt;
    if (this.#captureStage === 'out' && this.#captureElapsed >= CAPTURE.blackAt) {
      this.#captureRespawn?.(YARD_CLIMB_OUT);
      this.#captureRespawn = null;
      this.#fadeOverlay.setTarget(0);
      this.#captureStage = 'in';
      this.#captureElapsed = 0;
    } else if (this.#captureStage === 'in' && this.#captureElapsed >= CAPTURE.backAt) {
      this.#captureStage = null;
    }
  }

  #sampleFarThunder(): number {
    return FAR_THUNDER_MIN + this.#farThunderRandom() * (FAR_THUNDER_MAX - FAR_THUNDER_MIN);
  }

  #sampleRaceClang(): number {
    return RACE_CLANG_MIN + this.#raceClangRandom() * (RACE_CLANG_MAX - RACE_CLANG_MIN);
  }
}
